import React, { useCallback, useEffect, useState } from 'react';
import { ELEMENT_SIZE, Grid, InputField, SelectField } from 'ht-styleguide';
import useHomeHubAnswerController from '@scenes/HomeHub/hooks/hook.answerController';
import { useHomeHubStoreActions } from '@scenes/HomeHub/store/client/homehub.store';
import { TQuestionAnswer, TTransientAnswer } from '@scenes/HomeHub/homehub.types';
import { SelectOptions } from 'types/base.types';

const DropdownSelector = () => {
  /* Store */
  const { setSelectedAnswersClientTransient } = useHomeHubStoreActions();
  /* Hooks */
  const { answers, selectedAnswers, answerSchemaNormalizer } = useHomeHubAnswerController();

  /* Hook Constants */
  const [serverSeedDataValue] = selectedAnswers;
  const [inputValue, setInputValue] = useState<SelectOptions<number | string> | string>(serverSeedDataValue);
  const [showInputField, setShowInputField] = useState<boolean>(Boolean(serverSeedDataValue?.custom_input));

  /* Constants */
  const selectionCustomOther = answers.find((answer: TQuestionAnswer) => answer.custom_input);

  /* Constants */
  const dropdownOptions = answers.map((o: any) => {
    const formattedOptions = { label: o.text, value: o.id };
    return formattedOptions;
  });

  /* -------- METHODS ------------ */
  /* Normalize the state in store. The footer needs this trigger */
  const setClientAnswerTransient = useCallback(
    (val?: TTransientAnswer) => {
      const formatAnswer = val ? answerSchemaNormalizer.selectedAnswers(selectedAnswers).write([val]) : [];

      setSelectedAnswersClientTransient(formatAnswer);
    },
    [answerSchemaNormalizer, selectedAnswers, setSelectedAnswersClientTransient],
  );

  const handleInputClose = () => {
    setShowInputField(false);
    setClientAnswerTransient();
    setInputValue('');
  };

  const handleDropdownChange = (val: SelectOptions<string | number>) => {
    const selectionIsCustomOther = selectionCustomOther?.id === val.value;
    const userSelected = selectionIsCustomOther ? { ...val, label: '' } : val;

    setShowInputField(selectionIsCustomOther);

    /* If a user selects 'other', it is a non-selection until they enter a value, manually */
    if (selectionIsCustomOther) {
      // clear out the input field
      setInputValue('');
      setClientAnswerTransient();
    } else {
      setInputValue(userSelected);
      setClientAnswerTransient(userSelected as TTransientAnswer);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEntry = e.target.value?.trim();

    if (inputEntry) {
      setClientAnswerTransient({ ...selectionCustomOther, text: inputEntry });
      setInputValue({ label: inputEntry, value: selectionCustomOther.id });
    } else {
      setClientAnswerTransient();
      setInputValue('');
    }
  };

  /*
     Seed the client state so the footer can get at whether we can move forward without filling anything out.
     Also, only fire this if the user has valid seed and its a fresh load.
  */
  useEffect(() => {
    if (serverSeedDataValue) {
      if (serverSeedDataValue.custom_input) {
        setClientAnswerTransient({ ...selectionCustomOther, text: serverSeedDataValue.label });
      } else {
        setClientAnswerTransient(serverSeedDataValue);
      }
    }
  }, [selectionCustomOther, serverSeedDataValue, setClientAnswerTransient]);

  return (
    <Grid.Fluid>
      <Grid.Row>
        <Grid.Column sm={4} md={4} mdOffset={2} lg={4} lgOffset={4}>
          {!showInputField && (
            <SelectField
              elementSize={ELEMENT_SIZE.L}
              onChange={handleDropdownChange}
              options={dropdownOptions}
              value={inputValue || ''}
              placeholder="Select Answer"
              reactSelectClassName="text-align-left"
            />
          )}
          {showInputField && (
            <InputField
              key="surveyDropDown"
              elementSize={ELEMENT_SIZE.L}
              inputWrapperClass="marginBottom-small"
              iconName="close"
              iconOnClick={handleInputClose}
              onChange={handleInputChange}
              value={((inputValue as SelectOptions<string>)?.label || '') as string}
              placeholder="Please specify"
            />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid.Fluid>
  );
};

export default DropdownSelector;
